import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiClient } from '../../api/client';
import { fmtPrice } from './Verdict';

const usd = (v) => `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(2)}$`;

/** Elle açılmış pozisyona tek dokunuşla stop: sistem seviyesi / kayıp sınırı / likidasyon tamponu → düzenlenebilir fiyat → Binance STOP_MARKET (closePosition). */
export default function ManualStopDialog({ position, onClose, onPlaced }) {
    const [sug, setSug] = useState(null);
    const [err, setErr] = useState(null);
    const [price, setPrice] = useState('');
    const [picked, setPicked] = useState(null);
    const [maxLoss, setMaxLoss] = useState('25');
    const [applied, setApplied] = useState(25);
    const [busy, setBusy] = useState(false);
    const sym = position?.symbol;
    const manualPrice = useRef(false);

    useEffect(() => {
        if (!sym) return;
        let alive = true;
        setErr(null);
        apiClient.get(`/account/positions/${sym}/stop-suggest`, { params: { max_loss_pct: applied } })
            .then(({ data }) => {
                if (!alive) return;
                setSug(data);
                const rec = data.options.find((o) => o.key === data.recommended) || data.options[0];
                if (!manualPrice.current) {  // elle yazılmış fiyatın üstüne yazma
                    setPicked(rec?.key || null);
                    setPrice(rec ? String(rec.price) : '');
                }
            })
            .catch((e) => alive && setErr(e?.response?.data?.detail || 'Öneri alınamadı'));
        return () => { alive = false; };
    }, [sym, applied]);

    useEffect(() => {  // kayıp sınırı: yazarken değil, 600 ms durunca uygula
        const n = Number(maxLoss);
        if (!(n >= 1 && n <= 90)) return undefined;
        const t = setTimeout(() => setApplied(n), 600);
        return () => clearTimeout(t);
    }, [maxLoss]);

    if (!position) return null;
    const long = position.side === 'LONG';
    const p = Number(price);
    const amt = sug?.amt || Math.abs(position.notional / (position.mark || position.entry));
    const entry = Number(sug?.entry || position.entry);
    const mark = Number(sug?.mark || position.mark || entry);
    const valid = p > 0 && (long ? p < mark : p > mark) && (!sug?.liq || (long ? p > sug.liq : p < sug.liq));
    const loss = valid ? (long ? (entry - p) : (p - entry)) * amt : null;
    const margin = entry * amt / Math.max(1, position.leverage || 1);
    const dist = valid ? Math.abs(mark - p) / mark * 100 : null;

    const place = async () => {
        setBusy(true);
        try {
            const { data } = await apiClient.post(`/account/positions/${sym}/stop`, { price: p });
            toast.success(`Stop konuldu: ${fmtPrice(data.price)} (olası kayıp ${usd(data.loss_usd)})`);
            onPlaced?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Stop konulamadı');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg" data-testid="manual-stop-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" /> {sym.replace('USDT', '')} {position.side} {position.leverage}x — stop koy</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">
                        Giriş {fmtPrice(entry)} · mark {fmtPrice(mark)}{sug?.liq ? ` · likidasyon ${fmtPrice(sug.liq)}` : ''}{sug?.current_stop ? ` · mevcut stop ${fmtPrice(sug.current_stop)}` : ' · stop YOK'}.
                        Binance'e STOP-MARKET (pozisyonu kapat) emri konur; bu senin pozisyonun, bot başka dokunmaz.
                    </DialogDescription>
                </DialogHeader>
                {err && <div className="text-xs text-rose-300" data-testid="manual-stop-error">{err}</div>}
                {!sug && !err && <div className="text-xs text-slate-400">Öneriler hesaplanıyor…</div>}
                {sug && (
                    <div className="space-y-3">
                        {sug.warning_tr && (
                            <div className="rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="manual-stop-warning">⚠ {sug.warning_tr}</div>
                        )}
                        <div className="space-y-1.5" data-testid="manual-stop-options">
                            {sug.options.map((o) => (
                                <button key={o.key} type="button" onClick={() => { manualPrice.current = false; setPicked(o.key); setPrice(String(o.price)); }} data-testid={`manual-stop-opt-${o.key}`}
                                        className={`w-full text-left rounded-md border px-3 py-2 text-xs ${picked === o.key ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-slate-700 hover:border-slate-500'}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <b className="text-slate-50">{o.label}</b>
                                        {o.key === sug.recommended && <span className="text-[10px] font-bold text-emerald-200 border border-emerald-400/40 rounded px-1">{sug.cap_exceeded ? 'EN AZ KAYIP' : 'ÖNERİLEN'}</span>}
                                        {o.loss_margin_pct != null && o.loss_margin_pct > sug.max_loss_pct && <span className="text-[10px] font-bold text-amber-200 border border-amber-400/40 rounded px-1">sınırın üstünde</span>}
                                        <span className="ml-auto font-mono text-slate-100">{fmtPrice(o.price)}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        fiyattan %{o.dist_pct} uzak · girişten {o.from_entry_pct}% · olası kayıp <b className="text-rose-300">−{Number(o.loss_usd).toFixed(2)}$</b>{o.loss_margin_pct != null ? ` (teminatın %${o.loss_margin_pct}'i)` : ''}{o.note ? ` · ${o.note}` : ''}
                                    </div>
                                </button>
                            ))}
                            {sug.options.length === 0 && <div className="text-xs text-amber-200">Uygun seviye bulunamadı — fiyatı elle gir.</div>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block text-[10px] uppercase tracking-wider text-slate-400">Stop fiyatı (düzenle)
                                <input type="number" step="any" value={price} onChange={(e) => { manualPrice.current = true; setPrice(e.target.value); setPicked(null); }} data-testid="manual-stop-price"
                                       className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1 text-sm font-mono text-slate-100 focus:outline-none focus:border-emerald-400" />
                            </label>
                            <label className="block text-[10px] uppercase tracking-wider text-slate-400">Kayıp sınırı (teminatın %'si)
                                <input type="number" min={1} max={90} value={maxLoss} onChange={(e) => setMaxLoss(e.target.value)} data-testid="manual-stop-maxloss"
                                       className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1 text-sm font-mono text-slate-100 focus:outline-none focus:border-emerald-400" />
                            </label>
                        </div>
                        <div className="text-xs" data-testid="manual-stop-summary">
                            {valid
                                ? <>Bu stop tetiklenirse kayıp <b className="text-rose-300">−{loss.toFixed(2)}$</b> ({margin ? `teminatın %${(loss / margin * 100).toFixed(1)}'i` : ''}) · fiyattan %{dist.toFixed(2)} uzakta</>
                                : <span className="text-amber-200">Fiyat {long ? 'mark’ın altında ve likidasyonun üstünde' : 'mark’ın üstünde ve likidasyonun altında'} olmalı.</span>}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-slate-600 text-slate-200" data-testid="manual-stop-cancel">Vazgeç</button>
                            <button type="button" disabled={!valid || busy} onClick={place} data-testid="manual-stop-confirm"
                                    className="text-xs font-bold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40">
                                {busy ? 'Konuluyor…' : valid ? `Stop koy · ${fmtPrice(p)}` : 'Stop koy'}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
